import { enumOrRaw } from '~/services/api/contracts/enumOrRaw'
import type { FieldNormalizerSpec } from '~/services/api/contracts/FieldNormalizerSpec'
import { Normalization } from '~/utils/normalization'

type Apply = (value: unknown) => unknown
type Fields = Record<string, unknown>
type ScalarKind =
  | 'trimmed'
  | 'trimmedOrNull'
  | 'blankToNull'
  | 'website'
  | 'int'
  | 'float'
  | 'bool'
  | 'intOrRaw'
  | 'numericString'
  | 'textOrEmpty'
  | 'intList'
  | 'assocList'

interface FieldStep {
  readonly keys: readonly string[]
  readonly apply: Apply
  readonly accepts: (value: unknown) => boolean
}

const acceptsAny = (): boolean => true
const acceptsNonNull = (value: unknown): boolean => value !== null

const numericStringOrRaw = (value: unknown): unknown =>
  typeof value === 'string' ? (Normalization.intValue(value, null) ?? value) : value

const SCALAR_APPLIERS: readonly { readonly kind: ScalarKind; readonly apply: Apply }[] = [
  { kind: 'trimmed', apply: (value) => Normalization.trimmedStringValue(value, '') },
  { kind: 'trimmedOrNull', apply: (value) => Normalization.trimmedStringValue(value) },
  { kind: 'blankToNull', apply: Normalization.blankToNull },
  { kind: 'website', apply: (value) => Normalization.websiteUrl(value) },
  { kind: 'int', apply: (value) => Normalization.intValue(value, null) },
  { kind: 'float', apply: (value) => Normalization.floatValue(value, null) },
  {
    kind: 'bool',
    apply: (value) => (Normalization.isBoolValue(value) ? Normalization.boolValue(value) : value)
  },
  { kind: 'intOrRaw', apply: (value) => Normalization.intValue(value, null) ?? value },
  { kind: 'numericString', apply: numericStringOrRaw },
  { kind: 'textOrEmpty', apply: (value) => (typeof value === 'string' ? value : '') },
  { kind: 'intList', apply: (value) => Normalization.intList(value) },
  { kind: 'assocList', apply: (value) => Normalization.listOfAssocArrays(value) }
]

const scalarSteps = (lists: Pick<FieldNormalizerSpec, ScalarKind>): FieldStep[] =>
  SCALAR_APPLIERS.map(({ kind, apply }) => ({
    keys: lists[kind] ?? [],
    apply,
    accepts: acceptsAny
  }))

const enumSteps = (enums: NonNullable<FieldNormalizerSpec['enums']>): FieldStep[] =>
  enums.map(({ field, options, skipNull }) => ({
    keys: [field],
    apply: (value) => enumOrRaw(value, options),
    accepts: skipNull === true ? acceptsNonNull : acceptsAny
  }))

const customSteps = (custom: NonNullable<FieldNormalizerSpec['custom']>): FieldStep[] =>
  custom.map(({ keys, apply, skipNull }) => ({
    keys,
    apply,
    accepts: skipNull === true ? acceptsNonNull : acceptsAny
  }))

const compileSteps = (spec: FieldNormalizerSpec): FieldStep[] =>
  [...scalarSteps(spec), ...enumSteps(spec.enums ?? []), ...customSteps(spec.custom ?? [])].filter(
    (step) => step.keys.length > 0
  )

const compileAlways = (spec: FieldNormalizerSpec): FieldStep[] => compileSteps({ ...spec.always })

const applyPresent = (fields: Fields, step: FieldStep): void => {
  for (const key of step.keys) {
    if (key in fields && step.accepts(fields[key])) fields[key] = step.apply(fields[key])
  }
}

const applyAlways = (fields: Fields, step: FieldStep): void => {
  for (const key of step.keys) fields[key] = step.apply(fields[key])
}

const copyFields = (fields: Fields): Fields => ({ ...fields })

// An explicit undefined means "not provided": an intersection contract throws on an undefined-valued key.
const withoutUndefined = (fields: Fields): Fields =>
  Object.fromEntries(Object.entries(fields).filter(([, value]) => value !== undefined))

export const fieldNormalizer = (spec: FieldNormalizerSpec): Apply => {
  const always = compileAlways(spec)
  const steps = compileSteps(spec)
  const prepare = spec.prepare ?? copyFields
  const records = (spec.records ?? []).map(({ key, spec: child }) => ({
    key,
    normalize: fieldNormalizer(child)
  }))
  const lists = (spec.lists ?? []).map(({ key, spec: child }) => ({
    key,
    normalize: fieldNormalizer(child)
  }))

  return (input) => {
    if (Normalization.isRecord(input)) {
      const fields = prepare(withoutUndefined(input))
      for (const step of always) applyAlways(fields, step)
      for (const step of steps) applyPresent(fields, step)
      for (const { key, normalize } of records) {
        if (Normalization.isRecord(fields[key])) fields[key] = normalize(fields[key])
      }
      for (const { key, normalize } of lists) {
        const entries = fields[key]
        if (Array.isArray(entries)) fields[key] = entries.map(normalize)
      }
      return fields
    }
    return input
  }
}
